import React, { useEffect, useRef, useState } from 'react';
import { ARC_INTERACTION, getStatusFillClass } from './arcVisualSystem';
import { uiBtn } from '../../ui/buttonClasses';
import type { MarkerStatus } from './arcMetrics';

type MetricType = 'catch' | 'finish' | 'gap' | 'slip';

/** Keep cell size stable; wrap to more rows as stroke count grows. */
const TIMELINE_CELL_PX = 14;
const TIMELINE_CELL_GAP_PX = 2;

interface TimelineGridProps {
  totalStrokes: number;
  currentStroke: number;
  strokeStatuses: MarkerStatus[];
  strokeTitles?: string[];
  onStrokeClick?: (strokeNumber: number) => void;
  selectedMetric?: MetricType;
  onMetricChange?: (metric: MetricType) => void;
}

const TimelineGrid: React.FC<TimelineGridProps> = ({
  totalStrokes,
  currentStroke,
  strokeStatuses,
  strokeTitles = [],
  onStrokeClick,
  selectedMetric,
  onMetricChange,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(() => {
    if (totalStrokes <= 1) return 1;
    return Math.min(totalStrokes, 24);
  });

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      if (w < 8) return;

      if (totalStrokes <= 1) {
        setColumnCount(1);
        return;
      }
      if (totalStrokes <= 12) {
        setColumnCount(totalStrokes);
        return;
      }

      const colsByWidth = Math.max(
        1,
        Math.floor((w + TIMELINE_CELL_GAP_PX) / (TIMELINE_CELL_PX + TIMELINE_CELL_GAP_PX))
      );
      const next = Math.min(totalStrokes, colsByWidth);
      setColumnCount(Math.max(1, next));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [totalStrokes]);

  const statusLegend = [
    {
      key: 'good' as const,
      label: 'Good',
      detail: 'Within target',
    },
    {
      key: 'caution' as const,
      label: 'Caution',
      detail: 'Near edge',
    },
    {
      key: 'bad' as const,
      label: 'Bad',
      detail: 'Outside target',
    },
    {
      key: 'na' as const,
      label: 'N/A',
      detail: 'Not measured',
    },
  ];

  const metricButtons: { key: MetricType; label: string }[] = [
    { key: 'catch', label: 'Catch ∠' },
    { key: 'finish', label: 'Finish ∠' },
    { key: 'gap', label: 'Catch Gap' },
    { key: 'slip', label: 'Finish Slip' },
  ];

  const getStatusColor = (status: MarkerStatus) => {
    switch (status) {
      case 'good':
        return getStatusFillClass('good');
      case 'caution':
        return getStatusFillClass('caution');
      case 'bad':
        return getStatusFillClass('bad');
      case 'na':
        return getStatusFillClass('na');
      default:
        return 'bg-rowing-gray-300';
    }
  };

  return (
    <div className="flex flex-col w-full gap-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-content-primary">Timeline</h3>
          <span className="text-[11px] font-medium text-content-muted">
            Stroke {currentStroke} / {totalStrokes}
          </span>
        </div>
        {selectedMetric && onMetricChange && (
          <div className="grid grid-cols-2 gap-2">
            {metricButtons.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => onMetricChange(key)}
                aria-pressed={selectedMetric === key}
                className={`${uiBtn('toggle', { pressed: selectedMetric === key, size: 'sm' })} min-h-[38px] text-center leading-tight`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        ref={gridRef}
        className="mx-auto grid w-full min-w-0 rounded-lg border border-border bg-surface p-2"
        style={{
          gridTemplateColumns: `repeat(${columnCount}, ${TIMELINE_CELL_PX}px)`,
          gap: `${TIMELINE_CELL_GAP_PX}px`,
          justifyContent: 'center',
        }}
      >
        {Array.from({ length: totalStrokes }, (_, i) => {
          const strokeNum = i + 1;
          const status = strokeStatuses[i] || 'good';
          const isCurrent = strokeNum === currentStroke;
          const isFilled = strokeNum <= currentStroke;

          return (
            <button
              key={i}
              type="button"
              onClick={() => onStrokeClick?.(strokeNum)}
              className={`relative h-[14px] w-[14px] rounded-[3px] border ${
                isFilled
                  ? `border-border ${getStatusColor(status)}`
                  : 'border-border bg-surface-elevated'
              } ${isCurrent ? `${ARC_INTERACTION.activeRingClass} z-10` : 'z-0'} hover:opacity-80 transition-opacity`}
              title={strokeTitles[i] ?? `Stroke ${strokeNum}: ${status}`}
              aria-label={`Stroke ${strokeNum}`}
            />
          );
        })}
      </div>

      <div className="pt-4 border-t border-border">
        <div className="flex flex-col gap-3 text-xs">
          <span className="text-content-secondary font-medium">Legend</span>
          <div className="grid grid-cols-2 gap-2">
            {statusLegend.map((item) => (
              <div
                key={item.key}
                className="flex items-start gap-2 rounded-md bg-surface px-2.5 py-2 border border-border"
              >
                <div
                  className={`mt-0.5 h-3.5 w-3.5 rounded-sm ${getStatusColor(item.key)} border border-border flex-shrink-0`}
                ></div>
                <div className="leading-tight">
                  <div className="font-medium text-content-primary">{item.label}</div>
                  <div className="text-content-muted">{item.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineGrid;
